import { useState, useEffect } from 'react';

export interface LeaderboardItem {
  id: number;
  rank: number;
  name: string;
  initials: string;
  points: number;
  isCurrentUser: boolean;
}

export interface LeaderboardResponse {
  rankings: LeaderboardItem[];
  user_points: number;
  current_league: string;
}

/**
 * Custom React Hook to fetch Student Leaderboard data from WordPress REST API.
 * 
 * @param level Optional class level filter (e.g., ssc_2027)
 * @param group Optional academic group filter (e.g., science)
 * @param league Optional league filter (e.g., bronze)
 */
export function useLeaderboard(level?: string, group?: string, league?: string) {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;
    const shAuth = (window as any).shAuth;
    
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        if (level && level !== 'all') {
          queryParams.append('level', level);
        }
        if (group && group !== 'all') {
          queryParams.append('group', group);
        }
        if (league) {
          queryParams.append('league', league);
        }

        const url = `/wp-json/chorcha/v1/leaderboard?${queryParams.toString()}`;
        console.log(`[useLeaderboard] Fetching ranks from: ${url}`);
        
        const headers: Record<string, string> = {};
        if (shAuth?.rest_nonce) {
          headers['X-WP-Nonce'] = shAuth.rest_nonce;
        }

        const response = await fetch(url, { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch student rankings: ${response.statusText}`);
        }
        
        const rankings = await response.json();
        
        if (active) {
          setData(rankings);
          setError(null);
        }
      } catch (err) {
        console.error('[-] Leaderboard fetch error:', err);
        if (active) {
          setError(err as Error);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchLeaderboard();

    return () => {
      active = false;
    };
  }, [level, group, league]);

  return { data, loading, error };
}
